import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Table } from "antd";
import dayjs from "dayjs";
import { BarChart2, Users } from "react-feather";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import InfoTile from "../../../components/common/InfoTile";
import PrimaryCell from "../../../components/common/PrimaryCell";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { BRAND } from "../../dashboard/chartColors";
import { useGetCollectorMonthlySummaryQuery, money, tidy, customerKey } from "../collectorListsApi";

// Batch209 (2026-10-13): "Collection performance another one monthly collection summary. Show
// collector wise monthly client name then chart Jan to Dec then show payment collected monthly."
//
// Collector = whoever recorded the collection (money actually collected), not the list owner -
// that comparison is the Collector Performance page. Web records start August 2026, so earlier
// months of 2026 are simply empty.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const FIRST_YEAR = 2026;

const selectStyles = {
  container: (base) => ({ ...base, minWidth: 280 }),
  control: (base) => ({ ...base, minHeight: 34 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

const blankMonths = () => Array(12).fill(0);
const sum = (arr) => arr.reduce((s, v) => s + v, 0);
const cell = (v) => (v ? money(v) : <span className="text-muted">-</span>);
const compact = (v) =>
  v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${Math.round(v / 1e3)}K` : String(Math.round(v));

const monthColumns = (get) =>
  MONTHS.map((m, i) => ({ title: m, key: m, align: "right", render: (_, r) => cell(get(r)[i]) }));

const CollectorMonthlySummaryPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const thisYear = dayjs().year();
  const [year, setYear] = useState(thisYear);
  const [collectorGuid, setCollectorGuid] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const detailRef = useRef(null);

  const branchId = selectedBranchId ?? undefined;
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  const { data: rows = [], isFetching, isError, error } = useGetCollectorMonthlySummaryQuery({ year, branchId });

  // One entry per collector: months[12], total, and their clients (each with months[12]).
  const collectors = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = r.collectorEmployeeRecordGuid;
      if (!map.has(key)) {
        map.set(key, {
          guid: key,
          name: tidy(r.collectorName) || `Employee ${r.collectorEmpCode ?? ""}`.trim(),
          code: r.collectorEmpCode,
          months: blankMonths(),
          collections: 0,
          clients: new Map(),
        });
      }
      const c = map.get(key);
      const amount = Number(r.amount || 0);
      c.months[r.monthNo - 1] += amount;
      c.collections += r.collections;
      const ck = customerKey(r);
      if (!c.clients.has(ck)) {
        c.clients.set(ck, {
          key: ck,
          custId: r.custId,
          name: tidy(r.custName) || `Customer ${r.custId}`,
          branchCode: r.customerBranchCode,
          months: blankMonths(),
          collections: 0,
        });
      }
      const cl = c.clients.get(ck);
      cl.months[r.monthNo - 1] += amount;
      cl.collections += r.collections;
    });
    return [...map.values()]
      .map((c) => ({
        ...c,
        total: sum(c.months),
        clients: [...c.clients.values()]
          .map((cl) => ({ ...cl, total: sum(cl.months) }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  // Default to the top collector; drop a selection that is no longer in the data.
  useEffect(() => {
    if (!collectors.length) return;
    if (!collectorGuid || !collectors.some((c) => c.guid === collectorGuid)) setCollectorGuid(collectors[0].guid);
  }, [collectors, collectorGuid]);

  const selected = collectors.find((c) => c.guid === collectorGuid) || null;
  const grandMonths = useMemo(() => {
    const m = blankMonths();
    collectors.forEach((c) => c.months.forEach((v, i) => { m[i] += v; }));
    return m;
  }, [collectors]);
  const grandTotal = sum(grandMonths);
  const clientCount = useMemo(() => new Set(rows.map(customerKey)).size, [rows]);

  const visibleClients = useMemo(() => {
    if (!selected) return [];
    const q = clientSearch.trim().toLowerCase();
    return q ? selected.clients.filter((c) => c.name.toLowerCase().includes(q) || String(c.custId).includes(q)) : selected.clients;
  }, [selected, clientSearch]);
  const visibleMonthTotals = useMemo(() => {
    const m = blankMonths();
    visibleClients.forEach((c) => c.months.forEach((v, i) => { m[i] += v; }));
    return m;
  }, [visibleClients]);

  const chartData = (selected?.months || blankMonths()).map((v, i) => ({ month: MONTHS[i], amount: v }));

  const openCollector = (guid) => {
    setCollectorGuid(guid);
    setClientSearch("");
    setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const yearOptions = [];
  for (let y = thisYear; y >= FIRST_YEAR; y -= 1) yearOptions.push(y);

  const filterLines = [`Year: ${year}`, branchCode ? `Branch: ${branchCode}` : "All branches"];

  // ---- table 1: collector x month ----
  const collectorTotalsRow = { guid: "__total", name: "Total", code: null, months: grandMonths, total: grandTotal, __total: true };
  const columnsCollectors = [
    {
      title: "Collector",
      key: "name",
      fixed: "left",
      render: (_, c) =>
        c.__total ? <strong>Total</strong> : <PrimaryCell primary={c.name} secondary={c.code ? `#${c.code} · ${c.clients.length} clients` : `${c.clients.length} clients`} />,
    },
    ...monthColumns((c) => c.months),
    { title: "Total", key: "total", align: "right", render: (_, c) => <strong>{money(c.total)}</strong> },
  ];
  const exportCollectors = [
    { header: "Collector", value: (c) => c.name },
    { header: "Code", value: (c) => c.code ?? "" },
    { header: "Clients", value: (c) => c.clients.length, align: "right" },
    ...MONTHS.map((m, i) => ({ header: m, value: (c) => c.months[i], align: "right" })),
    { header: "Total", value: (c) => c.total, align: "right" },
  ];
  const monthTotals = (months, total) => Object.fromEntries([...MONTHS.map((m, i) => [m, months[i]]), ["Total", total]]);

  // ---- table 2: the selected collector's clients x month ----
  const clientTotalsRow = { key: "__total", name: "Total", months: visibleMonthTotals, total: sum(visibleMonthTotals), __total: true };
  const columnsClients = [
    {
      title: "Client",
      key: "name",
      fixed: "left",
      render: (_, c) =>
        c.__total ? <strong>Total</strong> : <PrimaryCell primary={c.name} secondary={`${c.custId} · ${c.branchCode}`} />,
    },
    ...monthColumns((c) => c.months),
    { title: "Total", key: "total", align: "right", render: (_, c) => <strong>{money(c.total)}</strong> },
  ];
  const exportClients = [
    { header: "Client", value: (c) => c.name },
    { header: "Cust No.", value: (c) => c.custId },
    { header: "Branch", value: (c) => c.branchCode },
    ...MONTHS.map((m, i) => ({ header: m, value: (c) => c.months[i], align: "right" })),
    { header: "Total", value: (c) => c.total, align: "right" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Collector Monthly Summary</h4>
              <h6>What each collector collected from each client, January to December</h6>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body d-flex flex-wrap align-items-center gap-2">
            <label className="small text-muted mb-0">Year</label>
            <select className="form-select form-select-sm" style={{ width: 110, height: 34 }} value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Select
              classNamePrefix="react-select"
              styles={selectStyles}
              options={collectors.map((c) => ({ value: c.guid, label: `${c.name}${c.code ? ` (#${c.code})` : ""} - ${money(c.total)}` }))}
              value={selected ? { value: selected.guid, label: `${selected.name}${selected.code ? ` (#${selected.code})` : ""}` } : null}
              onChange={(v) => v && openCollector(v.value)}
              placeholder="Collector - type to search"
            />
            <small className="text-muted">Collector = who recorded the collection. Records start August 2026.</small>
          </div>
        </div>

        {isError && <div className="alert alert-danger">Could not load the report ({error?.message || "server error"}).</div>}

        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3"><InfoTile label={`Collected in ${year}`} tone="success" value={money(grandTotal)} /></div>
          <div className="col-6 col-md-3"><InfoTile label="Collectors" value={collectors.length} /></div>
          <div className="col-6 col-md-3"><InfoTile label="Clients collected from" value={clientCount} /></div>
          <div className="col-6 col-md-3">
            <InfoTile label="Collections recorded" value={collectors.reduce((s, c) => s + c.collections, 0)} />
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h5 className="mb-0 d-inline-flex align-items-center gap-2"><Users size={18} /> By collector and month</h5>
              <ListExportToolbar
                title={`Collector Monthly Summary ${year}`}
                subtitleLines={filterLines}
                branchCode={branchCode}
                columns={exportCollectors}
                rows={collectors}
                totals={{ Collector: "Total", ...monthTotals(grandMonths, grandTotal) }}
                orientation="landscape"
                fileName={`CollectorMonthlySummary_${year}`}
              />
            </div>
            <div className="table-responsive">
              <Table
                className="table datanew dataTable no-footer"
                size="small"
                columns={columnsCollectors}
                dataSource={collectors.length ? [...collectors, collectorTotalsRow] : []}
                rowKey="guid"
                loading={isFetching}
                pagination={false}
                scroll={{ x: 1300 }}
                locale={{ emptyText: `No collections recorded in ${year}` }}
                rowClassName={(c) => (c.__total ? "bg-light" : c.guid === collectorGuid ? "table-active" : "")}
                onRow={(c) => (c.__total ? {} : { onClick: () => openCollector(c.guid), style: { cursor: "pointer" } })}
              />
            </div>
          </div>
        </div>

        {selected && (
          <div className="card table-list-card" ref={detailRef}>
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <h5 className="mb-0 d-inline-flex align-items-center gap-2">
                  <BarChart2 size={18} /> {selected.name}
                  {selected.code ? <span className="text-muted small">#{selected.code}</span> : null}
                  <span className="text-muted small fw-normal">
                    · {selected.clients.length} clients · {money(selected.total)} in {year}
                  </span>
                </h5>
              </div>

              <div style={{ width: "100%", height: 260 }} className="mb-3">
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={compact} tick={{ fontSize: 12 }} width={56} />
                    <Tooltip formatter={(v) => [money(v), "Collected"]} />
                    <Bar dataKey="amount" fill={BRAND} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <input
                  type="search"
                  className="form-control form-control-sm"
                  style={{ maxWidth: 280 }}
                  placeholder="Search client name or number"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                />
                <ListExportToolbar
                  title={`Monthly Collection - ${selected.name} - ${year}`}
                  subtitleLines={[...filterLines, clientSearch ? `Search: ${clientSearch}` : null].filter(Boolean)}
                  branchCode={branchCode}
                  columns={exportClients}
                  rows={visibleClients}
                  totals={{ Client: "Total", ...monthTotals(visibleMonthTotals, sum(visibleMonthTotals)) }}
                  orientation="landscape"
                  fileName={`CollectorMonthly_${tidy(selected.name).replace(/\s+/g, "_")}_${year}`}
                />
              </div>
              <div className="table-responsive">
                <Table
                  className="table datanew dataTable no-footer"
                  size="small"
                  columns={columnsClients}
                  dataSource={visibleClients.length ? [...visibleClients, clientTotalsRow] : []}
                  rowKey="key"
                  pagination={false}
                  scroll={{ x: 1300 }}
                  locale={{ emptyText: "No clients match" }}
                  rowClassName={(c) => (c.__total ? "bg-light" : "")}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectorMonthlySummaryPage;
